/**
 * Gate Deviation Dialog Component
 *
 * Displays a scatter plot showing gate deviations for selected operations.
 * Features:
 * - Color-coded by operation type (arrival/departure/etc)
 * - Different markers for same/opposite direction
 * - Clickable tooltips that can be pinned to the chart
 * - Unit conversion support
 * - Interactive hover and click tooltips
 */

import React, { FC, Dispatch, SetStateAction, useMemo, useState, useEffect, useRef } from 'react';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';

import { DialogBody, DialogFooter, Button, Size } from '@blueprintjs/core-modern';
import { Dialog } from 'src/componentsModern';
import { useDataSelectors } from 'src/@operations/reducers';
import { OperationSummary } from 'src/app/gql/__generated__/hooks';
import { useConfigSelectors, useLanguageSelectors } from 'src/app/reducers';
import { getOperationTheme } from 'src/utils';
import { INPUT_RETURN_TYPES } from 'src/constants';

import { PinnedTooltip as PinnedTooltipType } from './types';
import { PinnedTooltip } from './PinnedTooltip';
import {
  convertGateIntersectionData,
  getOperationColor,
  isOppositeDirection,
  formatTooltipTime,
  calculateYAxisConfig,
  calculateXAxisConfig,
  exportChartAsImage,
} from './helpers';
import { CHART_CONSTANTS, MARKER_STYLES } from './constants';
import { TVerticalDistanceUnit, TDistanceUnit } from 'src/utils/interfaces';
import { GraphLoading } from '../TrackProfile/TrackProfileDialog.styles';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

export interface IGateDeviationGraph {
  selectedOperationIds: number[];
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const GateDeviationDialog: FC<IGateDeviationGraph> = ({
  selectedOperationIds,
  isOpen,
  setIsOpen,
}) => {
  // Get configuration and theme
  const configSelectors = useConfigSelectors();
  const units = configSelectors.getUnits();
  const selectedTrackTheme = configSelectors.getTheme('operations');
  const colorList = getOperationTheme(selectedTrackTheme);

  // Get translations
  const languageSelectors = useLanguageSelectors();
  const {
    fields: {
      operations: { time: timeString, acid: acidString },
      gates: { altitude: altitudeString, direction: directionString },
    },
    screens: {
      operations: { deviationGraph },
    },
    components: {
      dialogs: { closeDialog, gateDeviationHeader },
    },
  } = languageSelectors.getLanguage();

  // Get selected operations data
  const dataSelectors = useDataSelectors();
  const { data } = dataSelectors.getDataInformation();
  const operationsMap = data as Map<number, OperationSummary>;
  const selectedOperations: OperationSummary[] = selectedOperationIds
    .map(id => operationsMap?.get(id))
    .filter((operation): operation is OperationSummary => Boolean(operation));
  const gateName = selectedOperations[0]?.gateIntersections?.[0]?.gate?.name || '';

  // State for pinned tooltips (user can click to pin/unpin)
  const [pinnedTooltips, setPinnedTooltips] = useState<PinnedTooltipType[]>([]);

  // Chart management
  const chartRef = useRef<any>(null); // Reference to Chart.js instance
  const chartContainerRef = useRef<HTMLDivElement>(null); // Reference for export functionality

  // Trigger chart resize when dialog opens (Chart.js handles this automatically)
  useEffect(() => {
    if (isOpen && chartRef.current) {
      // Small delay to allow dialog animation to complete
      const timer = setTimeout(() => {
        chartRef.current?.resize();
      }, CHART_CONSTANTS.CHART_RESIZE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  /**
   * Exports the chart (including pinned tooltips) as a JPG image
   */
  const handleExportChart = async () => {
    if (chartContainerRef.current) {
      try {
        await exportChartAsImage(chartContainerRef.current);
      } catch (error) {
        console.error('Failed to export chart:', error);
      }
    }
  };

  const conversionSettings = {
    convertTo: units.distance,
    convertFrom: 'ft' as TDistanceUnit,
    returnValueType: INPUT_RETURN_TYPES.NUMBER,
  };

  const conversionSettingsVertical = {
    convertTo: units.distanceVertical,
    convertFrom: 'ft' as TVerticalDistanceUnit,
    returnValueType: INPUT_RETURN_TYPES.NUMBER,
  };

  /**
   * Generate chart datasets from selected operations
   * Each operation becomes a single data point with color and marker style
   */
  const chartData = useMemo(() => {
    const datasets = selectedOperations
      .map(operation => {
        const dataPoint = convertGateIntersectionData(
          operation,
          conversionSettings,
          conversionSettingsVertical
        );
        if (!dataPoint) {
          return null;
        }

        const color = getOperationColor(operation.operationType, colorList);
        const isOpposite = isOppositeDirection(operation);

        return {
          label: operation.acid,
          data: [dataPoint],
          backgroundColor: color,
          pointRadius: CHART_CONSTANTS.POINT_RADIUS,
          pointStyle: isOpposite ? MARKER_STYLES.OPPOSITE_DIRECTION : MARKER_STYLES.SAME_DIRECTION,
        };
      })
      .filter(Boolean);

    return { datasets };
  }, [selectedOperations, colorList, units, conversionSettings]);

  /**
   * Configure chart options including axis ranges and interactions
   * Axis ranges are calculated dynamically based on data with a 20% buffer
   */
  const options = useMemo(() => {
    const isFeet = units.distanceVertical === 'ft';

    // Calculate axis configurations from data
    const yAxisConfig = calculateYAxisConfig(
      selectedOperations,
      isFeet,
      conversionSettingsVertical
    );
    const xAxisConfig = calculateXAxisConfig(selectedOperations, isFeet, conversionSettings);

    return {
      responsive: true,
      maintainAspectRatio: true,
      animation: false as const,
      // Handle click to pin/unpin tooltips
      onClick: (event: any, elements: any[], chart: any) => {
        if (elements.length > 0) {
          const element = elements[0];
          const chartElement = chart.getDatasetMeta(element.datasetIndex).data[element.index];

          // Check if this tooltip is already pinned
          const existingIndex = pinnedTooltips.findIndex(
            t => t.datasetIndex === element.datasetIndex && t.dataIndex === element.index
          );

          // Only add if not already pinned (removal is handled by PinnedTooltip component)
          if (existingIndex === -1) {
            setPinnedTooltips([
              ...pinnedTooltips,
              {
                datasetIndex: element.datasetIndex,
                dataIndex: element.index,
                x: chartElement.x,
                y: chartElement.y,
              },
            ]);
          }
        }
      },
      plugins: {
        legend: {
          display: false, // Hide legend as each operation is individually labeled
        },
        tooltip: {
          // Disable hover tooltips when any tooltip is pinned
          enabled: pinnedTooltips.length === 0,
          mode: 'nearest' as const,
          intersect: true,
          callbacks: {
            title: () => '', // No title needed
            label: (context: any) => {
              const dataPoint = context.raw;
              return [
                `${acidString}: ${context.dataset.label}`,
                `${altitudeString}: ${dataPoint.altitude}${units.distanceVertical}`,
                `${directionString}: ${dataPoint.direction}`,
                `${timeString}: ${formatTooltipTime(dataPoint.time)}`,
              ];
            },
          },
          displayColors: false,
        },
      },
      scales: {
        // X-axis: Lateral deviation (centered at 0)
        x: {
          min: xAxisConfig.min,
          max: xAxisConfig.max,
          ticks: {
            stepSize: xAxisConfig.stepSize,
            // Force Chart.js to use our exact tick values for miles
            ...(xAxisConfig.ticks && {
              callback: function(value: any) {
                // Only show ticks that are in our explicit tick array
                return xAxisConfig.ticks!.includes(Number(value.toFixed(2))) ? value : '';
              },
            }),
          },
          // Force explicit tick generation for miles
          ...(xAxisConfig.ticks && {
            afterBuildTicks: function(axis: any) {
              axis.ticks = xAxisConfig.ticks!.map(value => ({ value }));
            },
          }),
          title: {
            display: true,
            text: `${deviationGraph.xAxisLabel.replace('{unit}', units.distance)}`,
          },
          grid: {
            // Emphasize the center line (0 deviation)
            color: (context: any) =>
              context.tick.value === 0
                ? CHART_CONSTANTS.CENTER_LINE_COLOR
                : CHART_CONSTANTS.GRID_LINE_COLOR,
            lineWidth: (context: any) =>
              context.tick.value === 0
                ? CHART_CONSTANTS.CENTER_LINE_WIDTH
                : CHART_CONSTANTS.GRID_LINE_WIDTH,
          },
        },
        // Y-axis: Altitude
        y: {
          min: yAxisConfig.min,
          max: yAxisConfig.max,
          ticks: {
            stepSize: yAxisConfig.stepSize,
          },
          title: {
            display: true,
            text: `${deviationGraph.yAxisLabel.replace('{unit}', units.distanceVertical)}`,
          },
        },
      },
    };
  }, [selectedOperations, units, pinnedTooltips, conversionSettings]);

  const [isLoadBuffer, setIsLoadBuffer] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadBuffer(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      isCloseButtonShown
      size={Size.LARGE}
      title={
        <h3 className={`bp5-heading`}>
          {gateDeviationHeader} {gateName && `- ${gateName}`}
        </h3>
      }>
      <DialogBody>
        <GraphLoading loading={isLoadBuffer} size="l">
          <div
            style={{
              width: '100%',
              height: '100%',
              minHeight: `${CHART_CONSTANTS.MIN_CHART_HEIGHT}px`,
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1rem',
            }}>
            <div
              ref={chartContainerRef}
              style={{ position: 'relative', width: '100%', height: '100%' }}>
              <Scatter ref={chartRef} data={chartData} options={options} />
              {pinnedTooltips.map((tooltip, index) => {
                const dataset = chartData.datasets[tooltip.datasetIndex];
                const dataPoint = dataset?.data[tooltip.dataIndex];

                if (!dataset || !dataPoint) {
                  return null;
                }

                return (
                  <PinnedTooltip
                    key={`${tooltip.datasetIndex}-${tooltip.dataIndex}`}
                    tooltip={tooltip}
                    dataPoint={dataPoint}
                    label={dataset.label}
                    distanceUnit={units.distanceVertical}
                    onRemove={() => setPinnedTooltips(pinnedTooltips.filter((_, i) => i !== index))}
                  />
                );
              })}
            </div>
          </div>
        </GraphLoading>
      </DialogBody>
      <DialogFooter>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            text={deviationGraph.clearTags}
            disabled={pinnedTooltips.length === 0}
            onClick={() => setPinnedTooltips([])}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              text={deviationGraph.exportButton}
              onClick={handleExportChart}
              disabled={isLoadBuffer}
            />
            <Button
              intent="primary"
              text={closeDialog}
              onClick={() => {
                setIsOpen(false);
              }}
            />
          </div>
        </div>
      </DialogFooter>
    </Dialog>
  );
};
